import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Moon, Sun, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Footer } from "@/components/Footer";

export default function Settings() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const isDark = theme !== "light";
  const isMember = user && user !== false && user.subscription_status === "active";
  const canDelete = confirmText.trim().toLowerCase() === "delete";

  const closeConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setConfirmText("");
  };

  const deleteAccount = async () => {
    if (deleting || !canDelete) return;
    setDeleting(true);
    try {
      await api.delete("/user/me");
      toast("Your account and all history have been permanently deleted.");
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      toast(formatApiErrorDetail(err.response?.data?.detail) || "Could not delete your account.");
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 px-6 sm:px-10 py-8">
        <header className="max-w-3xl mx-auto flex items-center justify-between">
          <button
            data-testid="settings-back-button"
            onClick={() => navigate("/begin")}
            className="flex items-center gap-2 text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 text-sm"
          >
            <ArrowLeft size={16} /> Begin
          </button>
          <span className="text-xs tracking-[0.35em] uppercase text-[var(--mirror-text-2)]">
            Settings
          </span>
        </header>

        <main className="max-w-3xl mx-auto mt-20">
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="font-serif-display font-light text-3xl sm:text-4xl lg:text-5xl tracking-tight"
          >
            Settings
          </motion.h1>

          {/* Appearance */}
          <section data-testid="settings-appearance" className="mt-14">
            <h2 className="text-xs tracking-[0.3em] uppercase text-[var(--mirror-text-3)]">
              Appearance
            </h2>
            <div className="mt-5 rounded-2xl border border-[var(--mirror-border)] p-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-[var(--mirror-text)]">Theme</p>
                <p className="mt-1 text-sm text-[var(--mirror-text-3)]">
                  {isDark ? "Dark — low-light contemplation" : "Light — bright and open"}
                </p>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-[var(--mirror-border)] p-1">
                <button
                  data-testid="settings-theme-dark"
                  onClick={() => setTheme("dark")}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs tracking-[0.15em] uppercase transition-colors duration-500 ${
                    isDark ? "bg-[var(--mirror-surface)] text-[var(--mirror-text)]" : "text-[var(--mirror-text-3)]"
                  }`}
                >
                  <Moon size={14} /> Dark
                </button>
                <button
                  data-testid="settings-theme-light"
                  onClick={() => setTheme("light")}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs tracking-[0.15em] uppercase transition-colors duration-500 ${
                    !isDark ? "bg-[var(--mirror-surface)] text-[var(--mirror-text)]" : "text-[var(--mirror-text-3)]"
                  }`}
                >
                  <Sun size={14} /> Light
                </button>
              </div>
            </div>
          </section>

          {/* Membership */}
          <section data-testid="settings-membership" className="mt-12">
            <h2 className="text-xs tracking-[0.3em] uppercase text-[var(--mirror-text-3)]">
              Membership
            </h2>
            <div className="mt-5 rounded-2xl border border-[var(--mirror-border)] p-6 flex items-center justify-between gap-4">
              <div>
                <p className="text-[var(--mirror-text)]" data-testid="settings-membership-status">
                  {isMember ? "Mirror Member" : "Free"}
                </p>
                <p className="mt-1 text-sm text-[var(--mirror-text-3)]">
                  {isMember
                    ? "Unlimited conversations, daily reflections, and Journey exports."
                    : "One complete reflection. Become a member to keep looking."}
                </p>
              </div>
              {isMember ? (
                <span
                  data-testid="settings-member-badge"
                  className="rounded-full border border-[var(--mirror-accent)] px-4 py-2 text-xs tracking-[0.15em] uppercase text-[var(--mirror-accent)]"
                >
                  Active
                </span>
              ) : (
                <button
                  data-testid="settings-upgrade-button"
                  onClick={() => navigate("/pricing")}
                  className="shrink-0 rounded-full bg-[var(--mirror-accent)] text-[#16151F] px-6 py-2.5 text-xs tracking-[0.2em] uppercase font-medium hover:opacity-90 transition-opacity duration-500"
                >
                  Become a member
                </button>
              )}
            </div>
          </section>

          {/* Account */}
          <section data-testid="settings-account" className="mt-12">
            <h2 className="text-xs tracking-[0.3em] uppercase text-[var(--mirror-text-3)]">
              Account
            </h2>
            <div className="mt-5 rounded-2xl border border-[var(--mirror-border)] p-6">
              {user && user !== false && (
                <div className="space-y-1">
                  {user.name && (
                    <p className="text-[var(--mirror-text)]" data-testid="settings-user-name">
                      {user.name}
                    </p>
                  )}
                  <p className="text-sm text-[var(--mirror-text-3)]" data-testid="settings-user-email">
                    {user.email}
                  </p>
                </div>
              )}

              <div className="mt-6 pt-6 border-t border-[var(--mirror-border)]">
                <p className="text-[var(--mirror-text)]">Delete Account &amp; History</p>
                <p className="mt-1 text-sm text-[var(--mirror-text-3)] max-w-md">
                  Permanently remove your account, every conversation, and all Journey entries.
                  This cannot be undone.
                </p>
                <button
                  data-testid="settings-delete-account-button"
                  onClick={() => setConfirmOpen(true)}
                  className="mt-5 inline-flex items-center gap-2 rounded-full border border-[rgba(200,80,80,0.5)] px-6 py-2 text-xs tracking-[0.2em] uppercase text-[#d98a8a] hover:bg-[rgba(200,80,80,0.1)] transition-colors duration-500"
                >
                  <Trash2 size={14} /> Delete Account &amp; History
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>

      <Footer />

      {confirmOpen && (
        <div
          data-testid="delete-confirm-modal"
          className="fixed inset-0 z-[10000] flex items-center justify-center px-6 bg-black/60 backdrop-blur-sm"
          onClick={closeConfirm}
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.4 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-2xl border border-[var(--mirror-border-strong)] bg-[var(--mirror-bg-2)] p-8"
          >
            <h3 className="font-serif-display text-2xl font-light text-[var(--mirror-text)]">
              Delete everything?
            </h3>
            <p className="mt-4 text-sm text-[var(--mirror-text-2)] leading-relaxed">
              This permanently deletes your account, all of your conversations, and every Journey
              entry. This action is <span className="text-[var(--mirror-text)]">irreversible</span> —
              nothing can be recovered.
            </p>

            <label className="mt-6 block text-xs tracking-[0.15em] uppercase text-[var(--mirror-text-3)]">
              Type "delete" to confirm
            </label>
            <input
              data-testid="delete-confirm-input"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="delete"
              className="mt-2 w-full rounded-lg bg-transparent border border-[var(--mirror-border)] focus:border-[var(--mirror-border-strong)] px-4 py-2.5 text-sm outline-none text-[var(--mirror-text)] transition-colors duration-500"
            />

            <div className="mt-8 flex items-center justify-end gap-4">
              <button
                data-testid="delete-cancel-button"
                onClick={() => setConfirmOpen(false)}
                disabled={deleting}
                className="text-sm tracking-wide text-[var(--mirror-text-3)] hover:text-[var(--mirror-text)] transition-opacity duration-500 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                data-testid="delete-confirm-button"
                onClick={deleteAccount}
                disabled={deleting || !canDelete}
                className="inline-flex items-center gap-2 rounded-full bg-[#b34a4a] hover:bg-[#a03f3f] px-6 py-2.5 text-xs tracking-[0.2em] uppercase text-white transition-colors duration-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Trash2 size={14} /> {deleting ? "Deleting…" : "Delete forever"}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
